import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import type { AccountDto, CatalogItemDto, CatalogItemKind } from '@hesabat/contracts';
import { api, ProblemError, UnauthorizedError } from './api';
import { formatMoney, parseAmount } from './money';
import Loading from './Loading';
import ItemWizard from './ItemWizard';
import { Icon } from './icons';
import Hint from './Hint';

interface Props {
  companyId: string;
  accounts: readonly AccountDto[];
  canEdit: boolean;
  /** `settings.inventory.enabled` — نوع الصنف وحساباه المخزنيان يظهران حينها فقط. */
  inventoryEnabled: boolean;
  /** الرصيد الافتتاحى فى معالج الصنف — `journal.manual` كشاشة المخزون. */
  canPostOpening: boolean;
  onSessionLost: () => void;
}

interface Draft {
  nameAr: string;
  unit: string;
  defaultPrice: string;
  revenueAccountCode: string;
  etaItemCode: string;
  etaItemType: '' | 'EGS' | 'GS1';
  etaUnitType: string;
  kind: CatalogItemKind;
  inventoryAccountCode: string;
  cogsAccountCode: string;
  isActive: boolean;
}

const EMPTY: Draft = {
  nameAr: '',
  unit: 'خدمة',
  defaultPrice: '',
  revenueAccountCode: '',
  etaItemCode: '',
  etaItemType: '',
  etaUnitType: '',
  kind: 'service',
  inventoryAccountCode: '',
  cogsAccountCode: '',
  isActive: true,
};

const ISSUE_LABELS: Record<string, string> = {
  INVOICE_REVENUE_ACCOUNT: 'حساب الإيراد يجب أن يكون حساب إيراد طرفيًا',
  TAX_RULE_NOT_APPROVED: 'القاعدة الضريبية غير معتمدة',
  INVENTORY_DISABLED: 'المخزون غير مفعّل فى الإعدادات',
  ITEM_INVENTORY_ACCOUNT: 'حساب المخزون يجب أن يكون حساب مخزون طرفيًا',
  ITEM_COGS_ACCOUNT: 'حساب التكلفة يجب أن يكون حساب تكلفة مبيعات طرفيًا',
  ITEM_HAS_STOCK_MOVEMENTS: 'للصنف حركات مخزون، فنوعه وحساب مخزونه ثابتان',
};

function messageOf(caught: unknown): string {
  if (caught instanceof ProblemError) {
    const details =
      caught.problem.issues?.map(
        (issue) => ISSUE_LABELS[issue.code] ?? issue.path ?? issue.message,
      ) ?? [];
    const head =
      caught.problem.status === 409
        ? 'يوجد صنف بنفس الاسم'
        : (caught.problem.detail ?? caught.problem.title);
    return head + (details.length ? ': ' + details.join('، ') : '');
  }
  return caught instanceof Error ? caught.message : String(caught);
}

/**
 * كتالوج الأصناف والخدمات: ما يُباع بسعره وحسابه الافتراضيين. القيم تُنسخ إلى سطر
 * الفاتورة عند الإصدار، فتعديل صنف لا يغيّر فاتورة صدرت. الصنف يوقف ولا يُحذف.
 */
export default function Catalog({
  companyId,
  accounts,
  canEdit,
  inventoryEnabled,
  canPostOpening,
  onSessionLost,
}: Props) {
  const [wizard, setWizard] = useState(false);
  const [items, setItems] = useState<CatalogItemDto[] | null>(null);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [editing, setEditing] = useState<string | null>(null);
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  const revenueAccounts = useMemo(
    () => accounts.filter((account) => account.accountClass === 'revenue' && account.isPostable),
    [accounts],
  );
  const inventoryAccounts = useMemo(
    () =>
      accounts.filter(
        (account) =>
          account.accountClass === 'asset' &&
          account.subledger === 'inventory' &&
          account.isPostable,
      ),
    [accounts],
  );
  const cogsAccounts = useMemo(
    () => accounts.filter((account) => account.accountClass === 'cogs' && account.isPostable),
    [accounts],
  );

  const handle = useCallback(
    (caught: unknown) => {
      if (caught instanceof UnauthorizedError) onSessionLost();
      else setError(messageOf(caught));
    },
    [onSessionLost],
  );

  const load = useCallback(() => {
    api.catalogItems(companyId).then(setItems).catch(handle);
  }, [companyId, handle]);

  useEffect(load, [load]);

  function update<K extends keyof Draft>(key: K, value: Draft[K]): void {
    setDraft((current) => ({ ...current, [key]: value }));
    setIdempotencyKey(crypto.randomUUID());
    setError(null);
    setSaved(null);
  }

  function edit(item: CatalogItemDto): void {
    setEditing(item.id);
    setDraft({
      nameAr: item.nameAr,
      unit: item.unit,
      defaultPrice: item.defaultPrice,
      revenueAccountCode: item.revenueAccountCode ?? '',
      etaItemCode: item.etaItemCode ?? '',
      etaItemType: item.etaItemType ?? '',
      etaUnitType: item.etaUnitType ?? '',
      kind: item.kind,
      inventoryAccountCode: item.inventoryAccountCode ?? '',
      cogsAccountCode: item.cogsAccountCode ?? '',
      isActive: item.isActive,
    });
    setError(null);
    setSaved(null);
  }

  function reset(): void {
    setEditing(null);
    setDraft(EMPTY);
    setIdempotencyKey(crypto.randomUUID());
  }

  const showKind = inventoryEnabled || (items ?? []).some((item) => item.kind === 'stock');
  const price = parseAmount(draft.defaultPrice);
  const ready = draft.nameAr.trim().length >= 2 && draft.unit.trim() !== '' && price !== null;

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!ready || price === null) return;
    setSaving(true);
    setError(null);
    const body = {
      nameAr: draft.nameAr.trim(),
      unit: draft.unit.trim(),
      defaultPrice: price,
      revenueAccountCode: draft.revenueAccountCode || null,
      etaItemCode: draft.etaItemCode.trim(),
      etaItemType: draft.etaItemType || null,
      etaUnitType: draft.etaUnitType.trim(),
      kind: draft.kind,
      inventoryAccountCode: draft.kind === 'stock' ? draft.inventoryAccountCode || null : null,
      cogsAccountCode: draft.kind === 'stock' ? draft.cogsAccountCode || null : null,
    };
    try {
      const item = editing
        ? await api.updateCatalogItem(companyId, editing, { ...body, isActive: draft.isActive })
        : await api.createCatalogItem(companyId, idempotencyKey, body);
      setSaved((editing ? 'حُفظ الصنف ' : 'أُضيف الصنف ') + item.nameAr);
      reset();
      load();
    } catch (caught) {
      handle(caught);
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      {canEdit && wizard && (
        <ItemWizard
          companyId={companyId}
          accounts={accounts}
          inventoryEnabled={inventoryEnabled}
          canPostOpening={canPostOpening}
          describeError={messageOf}
          onCreated={load}
          onClose={() => setWizard(false)}
          onSessionLost={onSessionLost}
        />
      )}
      {canEdit && !wizard && !editing && (
        <button type="button" className="ghost wizard-launch" onClick={() => setWizard(true)}>
          <Icon name="sparkle" size={18} />
          {inventoryEnabled ? 'صنف جديد بمعالج — مع مخزونه الافتتاحى' : 'صنف جديد بمعالج'}
        </button>
      )}
      {canEdit && !wizard && (
        <form className="panel" onSubmit={(event) => void submit(event)} noValidate>
          <h2>{editing ? 'تعديل صنف' : 'صنف أو خدمة جديدة'}</h2>
          <div className="form-row">
            <label className="grow">
              الاسم
              <input
                value={draft.nameAr}
                maxLength={200}
                onChange={(event) => update('nameAr', event.target.value)}
              />
            </label>
            <label>
              الوحدة
              <input
                value={draft.unit}
                maxLength={32}
                onChange={(event) => update('unit', event.target.value)}
              />
              <Hint>كيف تبيعه: قطعة، كيلو، متر، ساعة، علبة.</Hint>
            </label>
            <label>
              السعر الافتراضى
              <input
                inputMode="decimal"
                value={draft.defaultPrice}
                onChange={(event) => update('defaultPrice', event.target.value)}
              />
              <Hint>يُكتب تلقائيًا فى الفاتورة، ويمكنك تغييره فيها.</Hint>
            </label>
          </div>
          <div className="form-row">
            <label className="grow">
              حساب الإيراد
              <select
                value={draft.revenueAccountCode}
                onChange={(event) => update('revenueAccountCode', event.target.value)}
              >
                <option value="">الافتراضى (إيرادات الخدمات)</option>
                {revenueAccounts.map((account) => (
                  <option key={account.code} value={account.code}>
                    {account.code} — {account.nameAr}
                  </option>
                ))}
              </select>
              <Hint>
                أين يُسجَّل دخل بيع هذا الصنف. اترك «الافتراضى» إن لم تكن متأكدًا — هو الصحيح لمعظم
                الشركات.
              </Hint>
            </label>
            <label>
              كود الفاتورة الإلكترونية (اختيارى)
              <input
                value={draft.etaItemCode}
                maxLength={64}
                onChange={(event) => update('etaItemCode', event.target.value)}
              />
              <Hint>مطلوب فقط لو تُصدر فواتير إلكترونية: كود الصنف المسجّل فى منظومة الضرائب.</Hint>
            </label>
            <label>
              نوع الكود
              <select
                value={draft.etaItemType}
                onChange={(event) =>
                  update('etaItemType', event.target.value as Draft['etaItemType'])
                }
              >
                <option value="">— غير محدد —</option>
                <option value="EGS">EGS (كود داخلى مسجّل فى المنظومة)</option>
                <option value="GS1">GS1 (باركود عالمى)</option>
              </select>
              <Hint>
                GS1 لو على الصنف باركود عالمى، وEGS لو كود خاص بشركتك سجّلته فى منظومة الضرائب.
              </Hint>
            </label>
            <label>
              كود الوحدة فى المنظومة (مثل EA)
              <input
                value={draft.etaUnitType}
                maxLength={16}
                onChange={(event) => update('etaUnitType', event.target.value)}
              />
              <Hint>رمز الوحدة عند الضرائب: EA للقطعة، KGM للكيلو. اسأل محاسبك إن لم تعرفه.</Hint>
            </label>
            {(inventoryEnabled || draft.kind === 'stock') && (
              <label>
                النوع
                <select
                  value={draft.kind}
                  onChange={(event) => update('kind', event.target.value as CatalogItemKind)}
                >
                  <option value="service">خدمة (بلا رصيد)</option>
                  <option value="stock">صنف مخزنى</option>
                </select>
                <Hint>
                  «مخزنى» لو تشتريه وتخزّنه فيتابع البرنامج كميته، و«خدمة» لو لا كمية له (صيانة،
                  استشارة، نقل).
                </Hint>
              </label>
            )}
            {editing && (
              <label className="option">
                <input
                  type="checkbox"
                  checked={draft.isActive}
                  onChange={(event) => update('isActive', event.target.checked)}
                />
                <span>نشط</span>
                <Hint>ألغِ العلامة لإخفاء الصنف من الفواتير الجديدة دون حذف تاريخه.</Hint>
              </label>
            )}
          </div>
          {draft.kind === 'stock' && (
            <div className="form-row">
              <label className="grow">
                حساب المخزون
                <select
                  value={draft.inventoryAccountCode}
                  onChange={(event) => update('inventoryAccountCode', event.target.value)}
                >
                  <option value="">الافتراضى (مخزون بضاعة 114001)</option>
                  {inventoryAccounts.map((account) => (
                    <option key={account.code} value={account.code}>
                      {account.code} — {account.nameAr}
                    </option>
                  ))}
                </select>
                <Hint>
                  أين تُسجَّل قيمة البضاعة الموجودة عندك فى المخزن. اترك «الافتراضى» إن لم تكن
                  متأكدًا — هو الصحيح لمعظم الشركات.
                </Hint>
              </label>
              <label className="grow">
                حساب تكلفة المبيعات
                <select
                  value={draft.cogsAccountCode}
                  onChange={(event) => update('cogsAccountCode', event.target.value)}
                >
                  <option value="">الافتراضى (تكلفة بضاعة مباعة 511001)</option>
                  {cogsAccounts.map((account) => (
                    <option key={account.code} value={account.code}>
                      {account.code} — {account.nameAr}
                    </option>
                  ))}
                </select>
                <Hint>
                  أين تُسجَّل تكلفة البضاعة لحظة بيعها، ليظهر ربحك الحقيقى. اترك «الافتراضى» إن لم
                  تكن متأكدًا — هو الصحيح لمعظم الشركات.
                </Hint>
              </label>
            </div>
          )}
          {draft.kind === 'stock' && (
            <p className="sub">
              الصنف المخزنى يُتابع رصيده: الشراء يزيده، والبيع يصرفه بتكلفته ويرحّل تكلفة المبيعات
              مع الفاتورة. بعد أول حركة لا يتغير نوعه ولا حساب مخزونه. الحسابان الافتراضيان من
              الدليل المرجعى ينتظران اعتماد المحاسب.
            </p>
          )}
          <p className="sub">
            الضريبة الافتراضية للصنف تظهر هنا حين تُعتمد القواعد الضريبية للشركة. كود المنظومة يُحفظ
            فقط ولا يُرسل بعد.
          </p>
          {error && <p className="error">{error}</p>}
          {saved && <p className="sub">{saved}</p>}
          <div className="form-actions">
            <button type="submit" className="primary" disabled={saving || !ready}>
              {saving ? 'جارى الحفظ…' : editing ? 'حفظ التعديل' : 'إضافة الصنف'}
            </button>
            {editing && (
              <button type="button" className="ghost" onClick={reset}>
                إلغاء التعديل
              </button>
            )}
          </div>
        </form>
      )}

      <div className="panel">
        <h2>الأصناف والخدمات</h2>
        {!canEdit && error && <p className="error">{error}</p>}
        {items === null && <Loading />}
        {items?.length === 0 && <p className="sub">لا أصناف بعد.</p>}
        {items && items.length > 0 && (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>الاسم</th>
                  <th>الوحدة</th>
                  <th>السعر</th>
                  {showKind && <th>النوع</th>}
                  <th>حساب الإيراد</th>
                  <th>كود المنظومة</th>
                  <th>الحالة</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr
                    key={item.id}
                    className={canEdit ? 'clickable' : undefined}
                    tabIndex={canEdit ? 0 : undefined}
                    onClick={canEdit ? () => edit(item) : undefined}
                    onKeyDown={(event) => {
                      if (canEdit && event.key === 'Enter') edit(item);
                    }}
                  >
                    <td>{item.nameAr}</td>
                    <td>{item.unit}</td>
                    <td className="num-cell">{formatMoney(item.defaultPrice)}</td>
                    {showKind && (
                      <td>
                        {item.kind === 'stock'
                          ? 'مخزنى (' + (item.inventoryAccountCode ?? '') + ')'
                          : 'خدمة'}
                      </td>
                    )}
                    <td className="mono">{item.revenueAccountCode ?? 'الافتراضى'}</td>
                    <td className="mono">
                      {item.etaItemCode ? (item.etaItemType ?? '?') + ' ' + item.etaItemCode : '—'}
                      {item.etaUnitType ? ' / ' + item.etaUnitType : ''}
                    </td>
                    <td>{item.isActive ? 'نشط' : 'موقوف'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
